import React from 'react';
import logoMark from '../assets/buscadoutor-mark.png';

/**
 * BuscaDoutor logo with subtle modern motion.
 *
 * Pure visual component — wrap with <Link to="/"> at the call site if you need
 * navigation. Default animation is a 3.5s vertical float (CSS-only, GPU friendly).
 * On hover the mark scales up slightly and gains a soft teal glow.
 *
 * Props
 *   size: 'sm' | 'md' | 'lg' (default 'md')
 *   showWordmark: boolean (default true) — hide the "BuscaDoutor" text for tight spaces
 *   variant: 'default' | 'on-dark' (changes wordmark contrast for dark backgrounds)
 *   className: extra classes on the outer container
 *   testId: data-testid (default 'logo')
 */
export const Logo = ({
  size = 'md',
  showWordmark = true,
  variant = 'default',
  className = '',
  testId = 'logo',
}) => {
  const sizeMap = {
    sm: { mark: 'h-8', text: 'text-base md:text-lg' },
    md: { mark: 'h-11 md:h-12', text: 'text-lg md:text-xl' },
    lg: { mark: 'h-16 md:h-20', text: 'text-2xl md:text-3xl' },
  };
  const s = sizeMap[size] || sizeMap.md;
  const baseColor = variant === 'on-dark' ? 'text-white' : 'text-[#1A2421]';
  // On dark backgrounds, the teal primary loses contrast — switch to amber for legibility.
  const accentColor = variant === 'on-dark' ? 'text-amber-400' : 'text-primary';

  return (
    <span
      className={`logo-root inline-flex items-center gap-2 ${className}`}
      data-testid={testId}
    >
      <span
        role="img"
        aria-label="BuscaDoutor"
        className={`logo-mark ${s.mark} block select-none ${accentColor}`}
        style={{
          aspectRatio: '1 / 1',
          WebkitMaskImage: `url(${logoMark})`,
          maskImage: `url(${logoMark})`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          backgroundColor: 'currentColor',
        }}
      />
      {showWordmark && (
        <span
          className={`logo-wordmark ${s.text} font-bold tracking-tight`}
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          <span className={baseColor}>Busca</span>
          <span className={accentColor}>Doutor</span>
        </span>
      )}
    </span>
  );
};

export default Logo;
